import { NgTemplateOutlet } from '@angular/common';
import type { OnChanges, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  TemplateRef,
} from '@angular/core';
import type { ValidationErrors } from '@angular/forms';
import { AbstractControl } from '@angular/forms';
// eslint-disable-next-line local-rules/validate-freelancer-imports -- Direct import to avoid ESM chain: @freelancer/pipes barrel → PipesModule → @freelancer/auth → CJS/ESM error in prepare:bits
import { FreelancerAsyncPipeModule } from '@freelancer/pipes/async/async-pipe.module';
import type { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { IconColor, IconModule, IconSize } from '../icon';

@Component({
  standalone: true,
  imports: [FreelancerAsyncPipeModule, IconModule, NgTemplateOutlet],
  selector: 'fl-validation-error',
  template: `
    @if (
      (status$ | flAsync) &&
      control.invalid &&
      (control.touched || control.dirty)
    ) {
      <div class="ErrorContainer">
        @if (showIcon) {
          <fl-icon
            class="IconError mr-xxxsmall"
            [name]="'ui-warning'"
            [color]="isWarning ? IconColor.WARNING : IconColor.ERROR"
            [size]="IconSize.SMALL"
            [useIconFont]="true"
            [fillIconFont]="true"
          ></fl-icon>
        }
        <p
          class="ErrorText {{ isWarning ? 'text-warning' : 'text-error' }}"
          aria-live="polite"
          [attr.id]="id"
        >
          @if (customValidationError !== undefined) {
            <ng-container
              *ngTemplateOutlet="customValidationError"
            ></ng-container>
          } @else {
            {{ status$ | flAsync }}
          }
        </p>
      </div>
    }
  `,
  styleUrls: ['./validation-error.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValidationErrorComponent implements OnChanges {
  IconColor = IconColor;
  IconSize = IconSize;

  status$: Observable<string>;

  @Input() id?: string;
  @Input() control: AbstractControl;
  @Input() isWarning?: boolean = false;
  /** Displays error text based on the given error key */
  @Input() errorKey?: string;
  @Input() showIcon = true;
  /** If provided, the validation error will display the custom error template instead of the default error text */
  @Input() customValidationError?: TemplateRef<HTMLElement>;

  ngOnChanges(changes: SimpleChanges): void {
    if ('control' in changes) {
      this.status$ = this.control.statusChanges.pipe(
        startWith(this.control.status),
        map(() => {
          if (this.isMatDatePickerError(this.control.errors)) {
            return $localize`Please select a valid date.`;
          }

          if (
            this.errorKey &&
            this.control.errors &&
            this.errorKey in this.control.errors
          ) {
            return this.control.errors[this.errorKey];
          }
          const [error]: string[] = Object.values(this.control.errors || {});
          return error;
        }),
      );
    }
  }

  // Hack around Material's error format
  private isMatDatePickerError(errors: ValidationErrors | null): any {
    return (
      errors &&
      (errors.matDatepickerParse ||
        errors.matDatepickerMin ||
        errors.matDatepickerMax ||
        errors.matStartDateInvalid ||
        errors.matEndDateInvalid)
    );
  }
}
